import { CircleInfoIcon, CopyIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat } from "common";
import dayjs from "dayjs";
import React, { PropsWithChildren } from "react";
import { useFeedbackSystemInfo } from "../../hooks/useFeedbackSystemInfo";

const SystemInfoSection = ({ title, children }: PropsWithChildren<{ title: string }>) => {
    return (
        <div className="mb-5">
            <div className="mb-2 font-bold">{title}</div>
            <div>{children}</div>
        </div>
    );
};

interface SystemInfoLineProps {
    label: string;
    defaultValue?: string;
    value?: any;
}

const SystemInfoLine = ({ label, defaultValue = "N/A", value }: SystemInfoLineProps) => {
    return (
        <div className="flex justify-between">
            <span className="mr-2">{label}</span>
            <span>{value ?? defaultValue}</span>
        </div>
    );
};

const SystemInfoBody = () => {
    const sysInfo = useFeedbackSystemInfo();
    const now = dayjs();
    const currentDate = now.format(DateFormat.DATE_LONG);
    const currentTime = now.format(DateFormat.TIME_TZ);
    let releaseDate;
    let releaseTime;
    if (sysInfo?.appInfo.releaseDate) {
        const release = dayjs(sysInfo.appInfo.releaseDate);
        releaseDate = release.format(DateFormat.DATE_LONG);
        releaseTime = release.format(DateFormat.TIME_TZ);
    }

    const browser = sysInfo?.browser?.name ? `${sysInfo.browser.name} ${sysInfo.browser.version ?? ""}` : undefined;
    const os = sysInfo?.os?.displayName
        ? sysInfo?.os?.displayName
        : sysInfo?.os?.name
        ? `${sysInfo.os.name} ${sysInfo.os.version ?? ""}`
        : undefined;

    return (
        <div>
            <SystemInfoSection title="User Info">
                <SystemInfoLine label="Seller ID" value={sysInfo?.seller.id} />
                <SystemInfoLine label="Seller" value={sysInfo?.seller.name} />
                <SystemInfoLine label="User" value={sysInfo?.user.name} />
                <SystemInfoLine label="Date" value={currentDate} />
                <SystemInfoLine label="Time" value={currentTime} />
            </SystemInfoSection>
            <SystemInfoSection title="Env & App Version">
                <SystemInfoLine label="Environment" value={sysInfo?.appInfo.environment} />
                <SystemInfoLine label="Version" value={sysInfo?.appInfo.version} />
                <SystemInfoLine label="Release Date" value={releaseDate} />
                <SystemInfoLine label="Time " value={releaseTime} />
            </SystemInfoSection>
            <SystemInfoSection title="Browser">
                <SystemInfoLine label="Name" value={browser} />
                <SystemInfoLine label="Resolution" value={`${window.innerWidth} x ${window.innerHeight}`} />
                <SystemInfoLine label="OS" value={os} />
                <SystemInfoLine label="User Agent" value={<CopyIcon />} />
                <SystemInfoLine label="URL" value={<CopyIcon />} />
            </SystemInfoSection>
        </div>
    );
};

export const SystemInfoDisplay = ({ className }: { className?: string }) => {
    return (
        <div className={clsx("", className)}>
            <Tooltip placement="top" content={<SystemInfoBody />}>
                <CircleInfoIcon size="large" />
                <span className="ml-2 cursor-default text-md font-bold">System Info</span>
            </Tooltip>
        </div>
    );
};
